import React from 'react';
import { Container, Row, Col, Card, Button, Tabs, Tab } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FaShieldAlt, FaVirus, FaEnvelope, FaBan, FaCheckCircle, FaRocket, FaPhone,
  FaExternalLinkAlt, FaRobot, FaCog, FaCloudUploadAlt, FaNetworkWired,
  FaServer, FaMobileAlt, FaGlobe, FaChartLine, FaLock, FaClock, FaUsers,
  FaDatabase, FaFilter, FaTools, FaBrain, FaSearch
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext'; // Import du hook

const animationVariants = {
  fadeInUp: {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8 }
  },
  scaleIn: {
    initial: { opacity: 0, scale: 0.96 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.5 }
  }
};

const AntiSpamAntiVirus = () => {
  const { t } = useLanguage(); // Utilisation des traductions
  const [setHeroRef, heroInView] = useScrollAnimation(0.1);
  const [setOverviewRef, overviewInView] = useScrollAnimation(0.1);

  const scrollToOverview = () => {
    document.getElementById('antivirus-overview')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      {/* Hero */}
      <section
        ref={setHeroRef}
        className="hero-section-sii"
        style={{
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.92) 0%, rgba(71, 85, 105, 0.92) 100%)',
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={7}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                <motion.div {...animationVariants.scaleIn} transition={{ delay: 0.2 }} className="mb-4">
                  <span className="badge-sii">
                    <FaShieldAlt className="me-2" />
                    {t.antispam.hero.badge}
                  </span>
                </motion.div>

                <motion.h1
                  className="display-3 fw-bold mb-4 text-white"
                  initial={{ opacity: 0, x: -50 }}
                  animate={heroInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  style={{ fontSize: 'clamp(2rem, 5vw, 3.2rem)', lineHeight: '1.1' }}
                  dangerouslySetInnerHTML={{ __html: t.antispam.hero.title }}
                />

                <motion.p
                  className="lead text-light mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  style={{ fontSize: 'clamp(1rem, 2.5vw, 1.15rem)', maxWidth: 700, lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}
                >
                  {t.antispam.hero.subtitle}
                </motion.p>

                <motion.div
                  className="d-flex flex-wrap gap-3 mb-4"
                  initial={{ opacity: 0, y: 30 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.7 }}
                >
                  <Button
                    size="lg"
                    onClick={scrollToOverview}
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.2rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      background: '#d90000',
                      color: 'white',
                      border: 'none',
                      boxShadow: '0 4px 16px rgba(217, 0, 0, 0.3)'
                    }}
                  >
                    <FaRocket className="me-2" /> {t.antispam.hero.btnExplore}
                  </Button>
                  <Button
                    variant="outline-light"
                    size="lg"
                    href="tel:+21600000000"
                    style={{ borderRadius: '16px', padding: '1rem 2.2rem', fontWeight: 600, borderWidth: 2, background: 'rgba(255,255,255,0.1)' }}
                  >
                    <FaPhone className="me-2" /> {t.antispam.hero.btnContact}
                  </Button>
                </motion.div>
              </motion.div>
            </Col>

            <Col lg={5} className="mt-4 mt-lg-0">
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={heroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-center"
              >
                <div style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '24px',
                  padding: '2.4rem',
                  backdropFilter: 'blur(18px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <h3 className="text-white mb-3 fw-bold">{t.antispam.whyBox.title}</h3>
                  <div className="text-start">
                    {t.antispam.whyBox.features.map((feature, index) => (
                      <motion.div key={index} className="d-flex align-items-center mb-2" initial={{ opacity: 0, x: -20 }} animate={heroInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}>
                        <FaCheckCircle className="me-3 text-success" style={{ minWidth: 20 }} />
                        <span className="text-white">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section principale avec onglets */}
      <section id="antivirus-overview" className="py-5" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}>
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <motion.div
                ref={setOverviewRef}
                initial={{ opacity: 0, y: 30 }}
                animate={overviewInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                <h2 className="fw-bold mb-4" style={{ fontSize: '2.5rem', color: '#1e293b' }} dangerouslySetInnerHTML={{ __html: t.antispam.overview.title }} />
                <p className="text-muted" style={{ fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto' }}>
                  {t.antispam.overview.subtitle}
                </p>
              </motion.div>
            </Col>
          </Row>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={overviewInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Tabs defaultActiveKey="antivirus" id="protection-tabs" className="nav-justified mb-4">
              {/* Onglet FortiGuard Antivirus */}
              <Tab eventKey="antivirus" title={
                <span>
                  <FaVirus className="me-2" style={{ color: '#d90000' }} />
                  {t.antispam.tabs.av}
                </span>
              }>
                <Row className="g-4">
                  <Col lg={12}>
                    <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: 18 }}>
                      <Card.Body className="p-4">
                        <Row className="align-items-center">
                          <Col lg={8}>
                            <h4 className="fw-bold mb-3" style={{ color: '#1e293b' }}>
                              <FaVirus className="me-3" style={{ color: '#d90000' }} />
                              {t.antispam.avContent.mainTitle}
                            </h4>
                            <p className="text-muted mb-3" style={{ fontSize: '1.05rem', lineHeight: 1.6 }}>
                              {t.antispam.avContent.mainDesc}
                            </p>
                            <ul className="list-unstyled">
                              <li className="d-flex align-items-center mb-2">
                                <FaBrain className="text-danger me-2" />
                                <small><strong>CPRL (Content Pattern Recognition Language)</strong> - {t.antispam.avContent.list.cprl.split(' - ')[1]}</small>
                              </li>
                              <li className="d-flex align-items-center mb-2">
                                <FaRobot className="text-danger me-2" />
                                <small><strong>{t.antispam.avContent.list.ia.split(' - ')[0]}</strong> - {t.antispam.avContent.list.ia.split(' - ')[1]}</small>
                              </li>
                              <li className="d-flex align-items-center mb-2">
                                <FaClock className="text-danger me-2" />
                                <small><strong>{t.antispam.avContent.list.updates.split(' - ')[0]}</strong> - {t.antispam.avContent.list.updates.split(' - ')[1]}</small>
                              </li>
                            </ul>
                          </Col>
                          <Col lg={4} className="text-center">
                            <div style={{
                              width: '120px', height: '120px', borderRadius: '50%',
                              background: 'linear-gradient(135deg, #d90000 0%, #b91c1c 100%)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              margin: '0 auto', boxShadow: '0 8px 32px rgba(217,0,0,0.3)'
                            }}>
                              <FaShieldAlt size={48} color="white" />
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 18 }}>
                      <div style={{ height: '200px', backgroundImage: 'linear-gradient(45deg, rgba(217,0,0,0.8), rgba(30,41,59,0.8)), url(/images/AntiVirus/antivirus-protection.png)', backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '18px 18px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="text-center text-white">
                          <FaVirus size={48} className="mb-2" />
                          <h5 className="fw-bold">{t.antispam.avContent.featTitle}</h5>
                        </div>
                      </div>
                      <Card.Body className="p-4">
                        <h5 className="fw-bold mb-3">{t.antispam.avContent.featTitle}</h5>
                        <ul className="list-unstyled mb-0">
                          {t.antispam.avContent.featList.map((f, i) => (
                            <li key={i} className="d-flex align-items-start mb-2">
                              <FaCheckCircle className="text-danger me-2 mt-1" size={14} />
                              <small><strong>{f.split(' : ')[0]} :</strong> {f.split(' : ')[1]}</small>
                            </li>
                          ))}
                        </ul>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 18 }}>
                      <div style={{ height: '200px', backgroundImage: 'linear-gradient(45deg, rgba(10, 35, 89, 0.8), rgba(30,41,59,0.8)), url(/images/AntiVirus/cprl-technology.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '18px 18px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="text-center text-white">
                          <FaBrain size={48} className="mb-2" />
                          <h5 className="fw-bold">{t.antispam.avContent.cprlTitle}</h5>
                        </div>
                      </div>
                      <Card.Body className="p-4">
                        <h5 className="fw-bold mb-3">{t.antispam.avContent.cprlTitle}</h5>
                        <ul className="list-unstyled mb-0">
                          {t.antispam.avContent.cprlList.map((f, i) => (
                            <li key={i} className="d-flex align-items-start mb-2">
                              <FaCheckCircle className="text-danger me-2 mt-1" size={14} />
                              <small><strong>{f.split(' : ')[0]} :</strong> {f.split(' : ')[1]}</small>
                            </li>
                          ))}
                        </ul>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              {/* Onglet FortiGuard Anti-Spam */}
              <Tab eventKey="antispam" title={
                <span>
                  <FaEnvelope className="me-2" style={{ color: '#d90000' }} />
                  {t.antispam.tabs.as}
                </span>
              }>
                <Row className="g-4">
                  <Col lg={12}>
                    <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: 18 }}>
                      <Card.Body className="p-4">
                        <Row className="align-items-center">
                          <Col lg={8}>
                            <h4 className="fw-bold mb-3" style={{ color: '#1e293b' }}>
                              <FaBan className="me-3" style={{ color: '#d90000' }} />
                              {t.antispam.asContent.mainTitle}
                            </h4>
                            <p className="text-muted mb-3" style={{ fontSize: '1.05rem', lineHeight: 1.6 }}>
                              {t.antispam.asContent.mainDesc}
                            </p>
                            <ul className="list-unstyled">
                              <li className="d-flex align-items-center mb-2">
                                <FaFilter className="text-danger me-2" />
                                <small><strong>{t.antispam.asContent.list.dual.split(' - ')[0]}</strong> - {t.antispam.asContent.list.dual.split(' - ')[1]}</small>
                              </li>
                              <li className="d-flex align-items-center mb-2">
                                <FaCog className="text-danger me-2" />
                                <small><strong>{t.antispam.asContent.list.perso.split(' - ')[0]}</strong> - {t.antispam.asContent.list.perso.split(' - ')[1]}</small>
                              </li>
                              <li className="d-flex align-items-center mb-2">
                                <FaClock className="text-danger me-2" />
                                <small><strong>{t.antispam.asContent.list.updates.split(' - ')[0]}</strong> - {t.antispam.asContent.list.updates.split(' - ')[1]}</small>
                              </li>
                            </ul>
                          </Col>
                          <Col lg={4} className="text-center">
                            <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: 'linear-gradient(135deg, #d90000 0%, #b91c1c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', boxShadow: '0 8px 32px rgba(217,0,0,0.3)' }}>
                              <FaEnvelope size={48} color="white" />
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 18 }}>
                      <div style={{ height: '200px', backgroundImage: 'linear-gradient(45deg, rgba(217,0,0,0.8), rgba(30,41,59,0.8)), url(/images/AntiSpam/email-protection.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '18px 18px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="text-center text-white">
                          <FaFilter size={48} className="mb-2" />
                          <h5 className="fw-bold">{t.antispam.asContent.featTitle}</h5>
                        </div>
                      </div>
                      <Card.Body className="p-4">
                        <h5 className="fw-bold mb-3">{t.antispam.asContent.featTitle}</h5>
                        <ul className="list-unstyled mb-0">
                          {t.antispam.asContent.featList.map((f, i) => (
                            <li key={i} className="d-flex align-items-start mb-2">
                              <FaCheckCircle className="text-danger me-2 mt-1" size={14} />
                              <small><strong>{f.split(' : ')[0]} :</strong> {f.split(' : ')[1]}</small>
                            </li>
                          ))}
                        </ul>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 18 }}>
                      <div style={{ height: '200px', backgroundImage: 'linear-gradient(45deg, rgba(10, 35, 89, 0.8), rgba(30,41,59,0.8)), url(/images/AntiSpam/spam-management.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '18px 18px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="text-center text-white">
                          <FaTools size={48} className="mb-2" />
                          <h5 className="fw-bold">{t.antispam.asContent.opsTitle}</h5>
                        </div>
                      </div>
                      <Card.Body className="p-4">
                        <h5 className="fw-bold mb-3">{t.antispam.asContent.opsTitle}</h5>
                        <ul className="list-unstyled mb-0">
                          {t.antispam.asContent.opsList.map((f, i) => (
                            <li key={i} className="d-flex align-items-start mb-2">
                              <FaCheckCircle className="text-danger me-2 mt-1" size={14} />
                              <small><strong>{f.split(' : ')[0]} :</strong> {f.split(' : ')[1]}</small>
                            </li>
                          ))}
                        </ul>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </motion.div>

          {/* Section intégration Security Fabric */}
          <Row className="my-5">
            <Col lg={12}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                <Card className="border-0 shadow-lg" style={{ borderRadius: 20, background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: 'white' }}>
                  <Card.Body className="p-5">
                    <Row className="align-items-center">
                      <Col lg={6}>
                        <h4 className="fw-bold mb-3">{t.antispam.fabric.title}</h4>
                        <p className="mb-4" style={{ opacity: 0.9 }}>{t.antispam.fabric.desc}</p>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center gap-3">
                              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FaNetworkWired style={{ color: '#d90000' }} size={20} />
                              </div>
                              <div>
                                <h6 className="fw-semibold mb-1">FortiGate NGFW</h6>
                                <small style={{ opacity: 0.8 }}>{t.antispam.fabric.items.ngfw}</small>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center gap-3">
                              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FaEnvelope style={{ color: '#d90000' }} size={18} />
                              </div>
                              <div>
                                <h6 className="fw-semibold mb-1">FortiMail</h6>
                                <small style={{ opacity: 0.8 }}>{t.antispam.fabric.items.mail}</small>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center gap-3">
                              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FaMobileAlt style={{ color: '#d90000' }} size={18} />
                              </div>
                              <div>
                                <h6 className="fw-semibold mb-1">FortiClient</h6>
                                <small style={{ opacity: 0.8 }}>{t.antispam.fabric.items.client}</small>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center gap-3">
                              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FaSearch style={{ color: '#d90000' }} size={18} />
                              </div>
                              <div>
                                <h6 className="fw-semibold mb-1">FortiSandbox</h6>
                                <small style={{ opacity: 0.8 }}>{t.antispam.fabric.items.sandbox}</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col lg={6} className="text-center">
                        <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '20px', padding: '2rem', backdropFilter: 'blur(10px)' }}>
                          <FaNetworkWired size={64} style={{ color: '#d90000', marginBottom: '1rem' }} />
                          <h5 className="fw-bold mb-2">{t.antispam.fabric.items.fabricTitle}</h5>
                          <p className="mb-0" style={{ opacity: 0.8 }}>{t.antispam.fabric.items.fabricSub}</p>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>

          {/* Cas d'usage */}
          <Row className="g-4 mb-5">
            <Col lg={12} className="text-center mb-4">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                <h4 className="fw-bold mb-2">{t.antispam.useCases.title}</h4>
                <p className="text-muted">{t.antispam.useCases.subtitle}</p>
              </motion.div>
            </Col>
            {t.antispam.useCases.list.map((usecase, idx) => {
              const icons = [FaNetworkWired, FaMobileAlt, FaCloudUploadAlt, FaEnvelope, FaShieldAlt, FaUsers];
              const colors = ['#d90000', '#1e293b', '#d90000', '#1e293b', '#d90000', '#1e293b'];
              const Icon = icons[idx];
              return (
                <Col key={idx} lg={4} md={6}>
                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: idx * 0.1 }} viewport={{ once: true }}>
                    <Card className="h-100 border-0 shadow-sm text-center" style={{ borderRadius: 16, transition: 'transform 0.3s' }} 
                          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-5px)'}
                          onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0px)'}>
                      <Card.Body className="p-4">
                        <div style={{ width: 60, height: 60, borderRadius: '50%', background: `linear-gradient(135deg, ${colors[idx]}15, ${colors[idx]}08)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto' }}>
                          <Icon style={{ color: colors[idx] }} size={24} />
                        </div>
                        <h6 className="fw-bold mb-2">{usecase.title}</h6>
                        <small className="text-muted">{usecase.desc}</small>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* Section Solutions de Sécurité (Piliers réutilisés) */}
      <section className="security-solutions-section-sii" style={{ padding: '8rem 0', background: `linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(71, 85, 105, 0.95) 100%), url('/images/security-bg.jpg')`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"%3003\"%3E%3Ccircle cx=\"30\" cy=\"30\" r=\"2\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")', opacity: 0.4 }} />
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row>
            <Col lg={12} className="text-center mb-5">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                <h2 className="section-title-sii" style={{ fontSize: '2.8rem', fontWeight: 700, color: '#ffffff', marginBottom: '1.5rem', textShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
                    dangerouslySetInnerHTML={{ __html: t.loadbalancer.security.title }} />
                <p className="section-subtitle-sii" style={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.9)', maxWidth: '700px', margin: '0 auto', lineHeight: '1.6', textShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                  {t.loadbalancer.security.subtitle}
                </p>
              </motion.div>
            </Col>
          </Row>

          <Row className="g-4">
            {[
              { id: 'fw', icon: FaShieldAlt, link: '/solutions/firewall' },
              { id: 'waf', icon: FaGlobe, link: '/solutions/waf' },
              { id: 'cyber', icon: FaLock, link: '/solutions/cyber-security' }
            ].map((sol, index) => {
              const data = t.loadbalancer.security.pillars[sol.id];
              return (
                <Col lg={4} md={6} key={sol.id}>
                  <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: index * 0.15 }} viewport={{ once: true }}
                    whileHover={{ y: -12, scale: 1.03 }}
                    style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '20px', padding: '2.5rem 2rem', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden', backdropFilter: 'blur(10px)' }}
                    onClick={() => { window.location.href = sol.link; }}
                  >
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #d90000, rgba(217,0,0,0.6))' }} />
                    <div style={{ background: 'linear-gradient(135deg, rgba(217,0,0,0.1), rgba(217,0,0,0.05))', borderRadius: '16px', width: '70px', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem auto' }}>
                      <sol.icon style={{ fontSize: '2rem', color: '#d90000' }} />
                    </div>
                    <div className="text-center">
                      <h4 style={{ fontWeight: 700, fontSize: '1.4rem', color: '#1a202c', marginBottom: '1rem' }}>{data.title}</h4>
                      <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '1.5rem' }}>{data.desc}</p>
                      <ul className="list-unstyled mb-3 text-start">
                        {data.features.map((feature, i) => (
                          <li key={i} className="d-flex align-items-start mb-2">
                            <FaCheckCircle style={{ color: '#d90000', marginRight: '0.75rem', marginTop: '0.125rem', fontSize: '0.8rem' }} />
                            <span style={{ fontSize: '0.85rem', color: '#4a5568' }}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <div style={{ paddingTop: '1rem', borderTop: '1px solid rgba(226, 232, 240, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d90000', fontSize: '0.9rem', fontWeight: 600 }}>
                        <span>{t.loadbalancer.security.learnMore}</span>
                        <FaExternalLinkAlt style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }} />
                      </div>
                    </div>
                  </motion.div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="cta-section-sii" style={{ padding: '6rem 0', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Container>
          <Row>
            <Col lg={12} className="text-center">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: 24, padding: '4rem 3rem', color: 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem', gap: '1rem' }}>
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaVirus size={24} color="#d90000" /></div>
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaEnvelope size={24} color="#d90000" /></div>
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaShieldAlt size={24} color="#d90000" /></div>
                </div>
                <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', fontSize: '2.2rem' }}>{t.antispam.cta.title}</h3>
                <p style={{ fontSize: '1.1rem', marginBottom: '2.2rem', opacity: 0.9, maxWidth: 800, margin: '0 auto 2.2rem auto' }}>{t.antispam.cta.desc}</p>
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <Button variant="light" size="lg" style={{ borderRadius: 16, padding: '1rem 2rem', fontWeight: 600 }} onClick={() => { window.location.href = '/#contact'; setTimeout(() => { document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); }}>
                    <FaPhone className="me-2" /> {t.antispam.cta.btnExpert}
                  </Button>
                  <Button variant="outline-light" size="lg" style={{ borderRadius: 16, padding: '1rem 2rem', fontWeight: 600, borderWidth: 2 }}>
                    <FaExternalLinkAlt className="me-2" /> {t.antispam.cta.btnDemo}
                  </Button>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default AntiSpamAntiVirus;